import { useState } from 'react'
import { PowerOff, ShieldCheck } from 'lucide-react'
import { useParams } from 'react-router-dom'
import Button from '@/components/button'
import ConfirmDialog from '@/components/confirm-dialog'
import EmptyState from '@/components/empty-state'
import PageHeader from '@/components/page-header'
import Skeleton from '@/components/skeleton'
import StatusBadge from '@/components/status-badge'
import Tabs from '@/components/tabs'
import { useAtualizarSituacaoCredencialMutation } from '@/http/mutations/credenciais'
import { useCredencialQuery } from '@/http/queries/credenciais'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { orDash } from '@/utility/Utils'
import AutorizacaoTab from './autorizacao'
import DadosGeraisTab from './dados-gerais'
import OperadoraTipoTab from './operadora-tipo'
import PermissoesTab from './permissoes'
import PlanosTabelaTab from './planos-tabela'
import SolicitanteExecutanteTab from './solicitante-executante'
import styles from './style.module.css'

const CredenciaisDetail = () => {
  const { credencialId } = useParams<{ credencialId: string }>()
  const { data: credencial, isPending } = useCredencialQuery(credencialId)
  const { can } = useAuth()
  const [abaAtiva, setAbaAtiva] = useState('dados-gerais')
  const [confirmandoSituacao, setConfirmandoSituacao] = useState(false)
  const atualizarSituacao = useAtualizarSituacaoCredencialMutation()

  if (isPending) {
    return (
      <>
        <PageHeader title="Credencial" backTo="/credenciais" />
        <Skeleton variant="text" count={6} />
      </>
    )
  }

  if (!credencial) {
    return (
      <>
        <PageHeader title="Credencial" backTo="/credenciais" />
        <EmptyState
          title="Credencial inexistente"
          description="Ela pode ter sido removida ou o identificador está incorreto."
        />
      </>
    )
  }

  const abas = [
    {
      id: 'dados-gerais',
      label: 'Dados gerais',
      content: <DadosGeraisTab credencial={credencial} />,
    },
    {
      id: 'operadora-tipo',
      label: 'Operadora e tipo',
      content: <OperadoraTipoTab credencial={credencial} />,
    },
    {
      id: 'autorizacao',
      label: 'Autorização',
      content: <AutorizacaoTab credencial={credencial} />,
    },
    {
      id: 'solicitante-executante',
      label: 'Solicitante e executante',
      content: <SolicitanteExecutanteTab credencial={credencial} />,
    },
    {
      id: 'permissoes',
      label: 'Permissões',
      content: <PermissoesTab credencial={credencial} />,
    },
    {
      id: 'planos-tabela',
      label: 'Planos e tabela',
      content: <PlanosTabelaTab credencial={credencial} />,
    },
  ]

  return (
    <>
      <PageHeader
        title={orDash(credencial.nome)}
        subtitle={
          <span className={styles.subtitle}>
            <StatusBadge
              label={credencial.ativa ? 'Ativa' : 'Inativa'}
              tone={credencial.ativa ? 'success' : 'neutral'}
            />
            <span>Prestador {orDash(credencial.codigoPrestador)}</span>
            <span className={styles.identificador}>ID {orDash(credencial.id)}</span>
          </span>
        }
        backTo="/credenciais"
        actions={
          can(ACAO.AlterarSituacaoCredencial) ? (
            <Button
              variant={credencial.ativa ? 'danger' : 'success'}
              iconLeft={credencial.ativa ? <PowerOff size={14} /> : <ShieldCheck size={14} />}
              onClick={() => setConfirmandoSituacao(true)}
            >
              {credencial.ativa ? 'Desativar' : 'Ativar'}
            </Button>
          ) : null
        }
      />

      <Tabs items={abas} activeId={abaAtiva} onChange={setAbaAtiva} />

      <ConfirmDialog
        isOpen={confirmandoSituacao}
        toggle={() => setConfirmandoSituacao(false)}
        title={credencial.ativa ? 'Desativar credencial' : 'Ativar credencial'}
        message={
          credencial.ativa
            ? `A credencial ${orDash(credencial.nome)} deixará de ser usada nas autorizações deste cliente. As guias que dependem dela param de ser autorizadas pelo crawler.`
            : `A credencial ${orDash(credencial.nome)} voltará a ser usada nas autorizações deste cliente.`
        }
        confirmLabel={credencial.ativa ? 'Desativar credencial' : 'Ativar credencial'}
        tone={credencial.ativa ? 'danger' : 'success'}
        isLoading={atualizarSituacao.isPending}
        onConfirm={() => {
          if (!credencial.id) return

          atualizarSituacao.mutate(
            { credenciaisId: credencial.id, ativa: !credencial.ativa },
            { onSuccess: () => setConfirmandoSituacao(false) },
          )
        }}
      />
    </>
  )
}

export default CredenciaisDetail
